import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Breadcrumbs,
  Link,
  Container
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import useForm from '../hooks/useForm';
import { safeLineColors } from '../theme/colors';

const initialForm = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

const faqs = [
  {
    question: 'How do I reset my password?',
    answer: 'Go to Settings > Security and click on "Reset Password". Follow the instructions sent to your email.'
  },
  {
    question: 'How do I contact emergency support?',
    answer: 'Call our emergency hotline at (800) 555-9111 for urgent issues.'
  },
  {
    question: 'Where can I find user guides?',
    answer: 'Visit the Help Center in the Settings menu for documentation and tutorials.'
  }
];

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: safeLineColors.backgrounds.pure,
    '& fieldset': {
      borderColor: safeLineColors.backgrounds.cool,
    },
    '&:hover fieldset': {
      borderColor: safeLineColors.primary.flame,
    },
    '&.Mui-focused fieldset': {
      borderColor: safeLineColors.primary.flame,
      borderWidth: 2
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: safeLineColors.primary.flame
  }
};

const HelpSupport = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, resetForm } = useForm(
    initialForm,
    ['name', 'email', 'subject', 'message'],
    (vals) => {
      setSnackbar({ open: true, message: 'Support request submitted successfully!', severity: 'success' });
      resetForm();
    }
  );

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const navigate = useNavigate();
  return (
    <Box sx={{ backgroundColor: safeLineColors.backgrounds.light, minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ 
            mb: 3,
            pl: 0.5,
            fontSize: 13,
            '& .MuiBreadcrumbs-separator': {
              color: safeLineColors.mutedGray
            }
          }}
        >
          <Link
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#000',
              fontSize: 16,
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.2s ease-in-out', // apply to color, decoration, etc.
              '&:hover': {
                color: safeLineColors.primary.flame,
                textDecoration: 'underline',
              }
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon 
              sx={{ 
                mr: 0.5, 
                fontSize: 16, 
                color: 'inherit', 
                transition: 'none',
                '& path': {
                  transition: 'none'
                }
              }} 
            />
            Dashboard
          </Link>

          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.primary.flame,
              fontWeight: 'medium',
              fontSize: 16
            }}
          >
            <SupportAgentIcon sx={{ mr: 0.5, fontSize: 16, color: safeLineColors.primary.flame }} />
            Help & Support
          </Typography>
        </Breadcrumbs>
        {/* Contact Support Card */}
        <Card
          elevation={3}
          sx={{
            maxWidth: 800,
            mx: 'auto',
            mb: 3,
            borderRadius: 2,
            backgroundColor: safeLineColors.backgrounds.pure,
            border: `1px solid ${safeLineColors.backgrounds.cool}`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SupportAgentIcon sx={{ color: safeLineColors.primary.flame, fontSize: 28, mr: 2 }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: safeLineColors.text.darkCharcoal }}>
                    Help & Support
                  </Typography>
                  <Typography variant="body2" sx={{ color: safeLineColors.text.mediumCharcoal }}>
                    Manage user help requests and contact support
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ backgroundColor: safeLineColors.backgrounds.pure, borderBottom: `1px solid ${safeLineColors.backgrounds.cool}`, pb: 2, pt: 2 }}
          />
          <CardContent sx={{ p: 4, pt: 0 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.name && touched.name}
                    helperText={touched.name && errors.name}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.email && touched.email}
                    helperText={touched.email && errors.email}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="subject"
                    label="Subject"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.subject && touched.subject}
                    helperText={touched.subject && errors.subject}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.message && touched.message}
                    helperText={touched.message && errors.message}
                    required
                    multiline
                    minRows={4}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 3, pt: 3, borderTop: `1px solid ${safeLineColors.backgrounds.cool}` }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      sx={{
                        fontWeight: 'bold',
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontSize: 16,
                        textTransform: 'none',
                        boxShadow: `0 4px 10px ${alpha(safeLineColors.primary.flame, 0.3)}`,
                        background: `linear-gradient(90deg, ${safeLineColors.primary.flame}, ${safeLineColors.primary.bright})`,
                        '&:hover': {
                          background: `linear-gradient(90deg, ${safeLineColors.primary.deep}, ${safeLineColors.primary.flame})`,
                          boxShadow: `0 6px 15px ${alpha(safeLineColors.primary.flame, 0.4)}`,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
        {/* FAQ Card */}
        <Card
          elevation={3}
          sx={{
            maxWidth: 800,
            mx: 'auto',
            mb: 0,
            borderRadius: 2,
            backgroundColor: safeLineColors.backgrounds.pure,
            border: `1px solid ${safeLineColors.backgrounds.cool}`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ color: safeLineColors.primary.flame, fontSize: 24, mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: safeLineColors.text.darkCharcoal }}>
                  Frequently Asked Questions
                </Typography>
              </Box>
            }
            sx={{ backgroundColor: safeLineColors.backgrounds.pure, borderBottom: `1px solid ${safeLineColors.backgrounds.cool}`, pb: 2, pt: 2 }}
          />
          <CardContent sx={{ p: 4, pt: 0 }}>
            <List sx={{ pl: 0 }}>
              {faqs.map((faq, idx) => (
                <ListItem key={idx} alignItems="flex-start" sx={{ pl: 0, mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <HelpOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600, fontSize: 17 }}>{faq.question}</Typography>}
                    secondary={<Typography variant="body2" color={safeLineColors.text.mediumCharcoal} sx={{ fontSize: 15 }}>{faq.answer}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, pl: 0 }}>
              <EmailIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color={safeLineColors.text.mediumCharcoal} sx={{ fontSize: 14, mr: 2 }}>
                support@safeline.com
              </Typography>
              <PhoneIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color={safeLineColors.text.mediumCharcoal} sx={{ fontSize: 14 }}>
                (800) 555-9111
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            iconMapping={{ success: <SupportAgentIcon /> }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default HelpSupport; 